import AccessDenied from "@/components/common/AccessDenied";
import InProcessStaffing from "@/components/staffing/modals/InProcessStaffing";
import PostingsSheet from "@/components/staffing/tabs/PostingsSheet";
import ApplicantsTab from "@/components/staffing/tabs/ApplicantsTab";
import ClientsTab from "@/components/staffing/tabs/ClientsTab";
import StaffTab from "@/components/staffing/tabs/StaffTab";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { StaffingView, useStaffing } from "@/context/StaffingContext";
import { Process } from "@/types/staffing/process";
import { ODataParams } from "@/utils/odataUtil";
import { buildPostingFilter, buildClientFilter, DEFAULT_CLIENT_STATUSES, DEFAULT_POSTING_FILTERS, PostingFilters, POSTING_STATUS_OPTIONS } from "@/utils/staffingUtil";
import { useContext, useEffect, useRef, useState } from "react";
import { PAGE_SIZE } from "@/utils/constants";
import { getRegionsWithFilters } from "@/services/regions/Regions";
import { Region } from "@/types/common/region";
import { useApiClient } from "@/context/ApiContext";
import SearchableDropdown, { DropdownOption } from "@/components/common/SearchableDropdown";
import { getPostingsColumns } from "@/components/staffing/columns/StaffingDashboardColumns";
import MultiSelect, { MultiSelectOption } from "@/components/common/MultiSelect";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getStaffingClientsWithFilters } from "@/services/client/Client";
import { getStaffingReasons } from "@/services/staffing/Staffing";
import { StaffingReasonOption } from "@/types/staffing/posting";

const POSTING_VIEWS: StaffingView[] = ['analyst', 'assistant_rbt', 'assessment', 'service_request'];

const perView = <T,>(value: T): Record<StaffingView, T> => ({
    analyst: value,
    assistant_rbt: value,
    assessment: value,
    service_request: value,
});

const StaffingDashboard = () => {
    useDocumentTitle('Staffing Dashboard');
    const [selectedTab, setSelectedTab] = useState<string>('clients');
    const [selectedProcesses, setSelectedProcesses] = useState<Process[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
    const [selectedPostingId, setSelectedPostingId] = useState<number | null>(null);
    const [processModalOpen, setProcessModalOpen] = useState(false);
    const [regions, setRegions] = useState<Region[]>([]);
    const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
    const { currentUser, accessControl } = useContext(CurrentUserContext);
    const { fetchPostings, setCurrentRegion } = useStaffing();
    const [postingFilters, setPostingFilters] = useState<Record<StaffingView, PostingFilters>>(perView(DEFAULT_POSTING_FILTERS));
    const [clientNameInputs, setClientNameInputs] = useState<Record<StaffingView, string>>(perView(''));
    const [reasonOptions, setReasonOptions] = useState<MultiSelectOption[]>([]);
    const lastFetchedFilters = useRef<Record<StaffingView, string | null>>(perView<string | null>(null));
    const apiClient = useApiClient();

    const isPostingView = POSTING_VIEWS.includes(selectedTab as StaffingView);
    const activeView = selectedTab as StaffingView;

    const updateActiveViewFilters = (update: Partial<PostingFilters>) => {
        setPostingFilters(prev => ({ ...prev, [activeView]: { ...prev[activeView], ...update } }));
    };

    const postingsColumns = getPostingsColumns((processes, clientId, clientName, postingId) => {
        setSelectedProcesses(processes);
        setSelectedClientId(clientId);
        setSelectedClientName(clientName);
        setSelectedPostingId(postingId);
        setProcessModalOpen(true);
    });

    useEffect(() => {
        const params: ODataParams = {
            select: ['Id', 'Region'],
            orderby: 'Region asc'
        };
        getRegionsWithFilters(params, apiClient)
            .then((res) => {
                const regionList = res.data.value as Region[];
                setRegions(regionList);
                const usersRegion = regionList.find(x => x.Region === currentUser.Region);
                if (usersRegion) {
                    setSelectedRegion(usersRegion);
                    setCurrentRegion(usersRegion.Region);
                }
            })
            .catch((err) => console.error(err));
    }, [currentUser.Region, apiClient, setCurrentRegion]);

    // Staffing Reasons filter vocabulary (task 10639): reasons stamped on at least one non-closed
    // posting, server-cached ~5 min. Region-independent, so it loads once. MultiSelect values are
    // strings; the reason id is round-tripped through String()/Number() at the boundary.
    useEffect(() => {
        getStaffingReasons(apiClient)
            .then(res => {
                const reasons = (Array.isArray(res.data) ? res.data : []) as StaffingReasonOption[];
                setReasonOptions(reasons.map(r => ({ label: r.title, value: String(r.id) })));
            })
            .catch(err => console.error('Error fetching staffing reasons:', err));
    }, [apiClient]);

    useEffect(() => {
        if (!isPostingView) return;
        const view = selectedTab as StaffingView;
        const t = setTimeout(() => {
            const name = clientNameInputs[view].trim();
            if (!name) {
                setPostingFilters(prev => prev[view].clientIds.length === 0
                    ? prev
                    : { ...prev, [view]: { ...prev[view], clientIds: [] } });
                return;
            }
            const filter = buildClientFilter(selectedRegion?.Region ?? '', {
                name,
                statuses: DEFAULT_CLIENT_STATUSES,
                enrollmentStatuses: [],
                insuranceCompanies: [],
                cities: [],
            });
            getStaffingClientsWithFilters({ filter, select: ['Id'] }, apiClient)
                .then(res => {
                    const ids: number[] = (res.data.value as { Id: number }[]).map(c => c.Id);
                    const next = ids.length ? ids : [0];
                    setPostingFilters(prev => {
                        const current = prev[view].clientIds;
                        if (current.length === next.length && current.every((id, i) => id === next[i])) return prev;
                        return { ...prev, [view]: { ...prev[view], clientIds: next } };
                    });
                })
                .catch(err => console.error(err));
        }, 300);
        return () => clearTimeout(t);
    }, [clientNameInputs, selectedTab, isPostingView, selectedRegion, apiClient]);

    useEffect(() => {
        if (!selectedRegion) return;
        POSTING_VIEWS.forEach((view) => {
            const filter = buildPostingFilter(selectedRegion.Region, view, postingFilters[view]);
            if (lastFetchedFilters.current[view] === filter) return;
            lastFetchedFilters.current[view] = filter;
            const params: ODataParams = {
                filter,
                expand: ['processes,client'],
                orderby: 'id desc'
            };
            fetchPostings(view, params);
        });
    }, [selectedRegion, postingFilters, fetchPostings]);

    if (!accessControl.canRead('staffing_dashboard')) {
        return <AccessDenied />;
    }

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold">Staffing Dashboard</h1>
                <SearchableDropdown
                    options={regions.map((r): DropdownOption => ({ label: r.Region, value: r.Region }))}
                    value={selectedRegion?.Region ?? null}
                    onChange={(val) => {
                        const region = regions.find(r => r.Region === val) ?? null;
                        setSelectedRegion(region);
                        setCurrentRegion(region?.Region ?? null);
                    }}
                    placeholder="Select region..."
                    searchPlaceholder="Search regions..."
                />
            </div>
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList className='mb-2'>
                    <div className="flex flex-col items-center ml-1 pl-2">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">People</span>
                        <div className="flex">
                            <TabsTrigger value='clients'>Clients</TabsTrigger>
                            <TabsTrigger value="staff">Staff</TabsTrigger>
                            <TabsTrigger value="applicants">Applicants</TabsTrigger>
                        </div>
                    </div>
                    <div className="flex flex-col items-center border-l ml-1 pl-2">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Postings</span>
                        <div className="flex">
                            <TabsTrigger value="analyst">Analyst</TabsTrigger>
                            <TabsTrigger value="assistant_rbt">Assistant/RBT</TabsTrigger>
                            <TabsTrigger value="assessment">Assessment</TabsTrigger>
                            <TabsTrigger value="service_request">Service Requests</TabsTrigger>
                        </div>
                    </div>
                </TabsList>
            </Tabs>
            {['analyst', 'assistant_rbt', 'assessment', 'service_request'].includes(selectedTab) ?
                <>
                    <div className="flex items-center gap-2 mb-2">
                        <Input
                            placeholder="Search by client..."
                            value={clientNameInputs[activeView]}
                            onChange={(e) => {
                                const value = e.target.value;
                                setClientNameInputs(prev => ({ ...prev, [activeView]: value }));
                            }}
                            className="w-48"
                        />
                        <MultiSelect
                            options={POSTING_STATUS_OPTIONS}
                            value={postingFilters[activeView].statuses}
                            onChange={(statuses) => updateActiveViewFilters({ statuses })}
                            placeholder="Status"
                            searchPlaceholder="Search statuses..."
                        />
                        <MultiSelect
                            options={reasonOptions}
                            value={postingFilters[activeView].reasonIds.map(String)}
                            onChange={(values) => updateActiveViewFilters({ reasonIds: values.map(Number) })}
                            placeholder="Staffing Reasons"
                            searchPlaceholder="Search reasons..."
                        />
                        {selectedTab !== 'service_request' && (
                            <div className="flex items-center gap-1.5">
                                <Checkbox
                                    id="srb-filter"
                                    checked={postingFilters[activeView].serviceRequestedByOnly}
                                    onCheckedChange={(checked) => updateActiveViewFilters({ serviceRequestedByOnly: !!checked })}
                                />
                                <Label htmlFor="srb-filter" className="text-sm cursor-pointer">Service Requests</Label>
                            </div>
                        )}
                    </div>
                    <PostingsSheet
                        key={selectedTab}
                        columns={postingsColumns}
                        view={selectedTab as StaffingView}
                        serverSide={{
                            pageSize: PAGE_SIZE,
                            onParamsChange: (sort, page) => {
                                const view = selectedTab as StaffingView;
                                fetchPostings(view, {
                                    filter: buildPostingFilter(selectedRegion?.Region ?? '', view, postingFilters[view]),
                                    expand: ['processes,client'],
                                    orderby: sort ? `${sort.key} ${sort.direction}` : 'id desc',
                                    skip: page.skip,
                                    top: page.top
                                });
                            }
                        }}
                        rowClassName={(row) =>
                            row.Status === 'Staffed and Billed'
                                ? 'bg-green-50 hover:bg-green-100/70 dark:bg-green-950 dark:hover:bg-green-900'
                                : undefined}
                    />
                </> :
                selectedTab === 'clients' ?
                    <ClientsTab region={selectedRegion?.Region ?? null} /> :
                    selectedTab === 'staff' ?
                        <StaffTab region={selectedRegion?.Region ?? null} /> :
                        selectedTab === 'applicants' ?
                            <ApplicantsTab region={selectedRegion?.Region ?? null} /> :
                            null
            }
            <InProcessStaffing
                processes={selectedProcesses}
                open={processModalOpen}
                onOpenChange={setProcessModalOpen}
                clientId={selectedClientId}
                clientName={selectedClientName}
                postingId={selectedPostingId}
            />
        </div>
    );
};

export default StaffingDashboard;
